import React, { useState, useEffect } from 'react';
import { FaVideo } from 'react-icons/fa';
import PageTitle from '../../components/PageTitle';
import api from '../../services/api';

interface Video {
  id: number | string;
  title: string;
  description: string;
  thumbnailUrl: string;
  youtubeUrl: string;
}

function getYoutubeVideoId(url: string): string {
  const match = url.match(/[?&]v=([^&]+)/) || url.match(/youtu[.]be\/([^?&#]+)/);
  return match ? match[1] : '';
}

function mapVideo(raw: any, index: number): Video | null {
  const youtubeUrl = raw?.youtubeUrl || raw?.youtube_url || '';
  if (!youtubeUrl) return null;
  const ytId = getYoutubeVideoId(youtubeUrl) || youtubeUrl.split('/').pop() || '';
  const thumbnailUrl =
    raw?.thumbnailUrl ||
    raw?.thumbnail_url ||
    (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : '');
  return {
    id: raw?.id ?? index,
    title: raw?.title || 'Untitled Video',
    description: raw?.description || '',
    thumbnailUrl,
    youtubeUrl,
  };
}

const VideoModal = ({ video, onClose }: { video: Video; onClose: () => void }) => {
  const videoId = getYoutubeVideoId(video.youtubeUrl);
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-5xl mx-auto bg-gray-800 rounded-lg overflow-hidden">
        <div className="relative pt-[56.25%]">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2"
            type="button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <iframe
            src={embedUrl}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute top-0 left-0 w-full h-full"
          />
        </div>
        <div className="p-4">
          <h3 className="text-xl font-semibold text-white mb-2">{video.title}</h3>
          {video.description ? <p className="text-gray-300">{video.description}</p> : null}
        </div>
      </div>
    </div>
  );
};

const VideoGalleryPage: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        setLoading(true);
        const response = await api.getVideoGalleries();
        const galleries = response.galleries || [];
        const flat: Video[] = [];
        galleries.forEach((gallery: any) => {
          (gallery.videos || []).forEach((raw: any, index: number) => {
            const v = mapVideo(raw, index);
            if (v) flat.push(v);
          });
        });
        setVideos(flat);
      } catch (error) {
        console.error('Error fetching video galleries:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGalleries();
  }, []);

  return (
    <div className="bg-gray-900 min-h-screen">
      <PageTitle
        title="Video Gallery"
        subtitle="Watch our latest videos and stay updated with our activities"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-12 bg-gray-800/50 rounded-xl">
            <div className="w-20 h-20 mx-auto mb-6 bg-purple-500/20 rounded-full flex items-center justify-center">
              <FaVideo className="w-10 h-10 text-purple-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">No Videos Available</h2>
            <p className="text-gray-400">Check back soon for our video galleries!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {videos.map((video) => (
              <div
                key={String(video.id)}
                className="group bg-gray-800/80 rounded-xl overflow-hidden border border-gray-700/50 hover:border-purple-500/50 transition-all duration-300 cursor-pointer"
                onClick={() => setSelectedVideo(video)}
              >
                <div className="relative aspect-video overflow-hidden bg-black">
                  {video.thumbnailUrl ? (
                    <img
                      src={video.thumbnailUrl}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-600">
                      <FaVideo className="w-8 h-8" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-xl">
                      <svg className="w-6 h-6 text-black translate-x-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                      </svg>
                    </div>
                  </div>
                  <span className="absolute bottom-2 left-2 text-[10px] px-1.5 py-0.5 bg-black/70 rounded text-gray-200">
                    YouTube
                  </span>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-semibold text-white line-clamp-1">{video.title}</h3>
                  {video.description ? (
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2">{video.description}</p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {selectedVideo ? (
        <VideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />
      ) : null}
    </div>
  );
};

export default VideoGalleryPage;
